<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, defineEmits, ref } from 'vue';

import CardComponent from '@/components/ui/CardComponent.vue';
import profileService from "@/services/profile/profileService.ts";

import type { TradingApiKeyResponse } from "@/types/services/profile/profile.type.ts";

const { t } = useI18n();

const emits = defineEmits(['closeModal']);

const props = withDefaults(defineProps<{
  title: string;
  message: string;
  isGenerateApiKey: boolean;
}>(), {
  isGenerateApiKey: false
});

const tradingApiKey = ref<string | null>(null);
const isKeyGenerated = ref<boolean>(false);
const copied = ref<boolean>(false);

const componentTitle = computed(() => {
  return props.isGenerateApiKey && isKeyGenerated.value
    ? t("tradingApiKeyComponent.generatedTitle")
    : props.title
});

const componentMessage = computed(() => {
  return props.isGenerateApiKey && isKeyGenerated.value
    ? t("tradingApiKeyComponent.generatedMessage")
    : props.message
});

const showCancelButton = computed(() => {
  return !isKeyGenerated.value;
});

const validateButtonText = computed(() => {
  if (isKeyGenerated.value) {
    return t('common.buttons.close');
  }
  return t('common.buttons.validate');
});

async function copyToClipboard(): Promise<void> {
  if (tradingApiKey.value) {
    await navigator.clipboard.writeText(tradingApiKey.value);

    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  }
}

async function handleValidate(): Promise<void> {
  if (props.isGenerateApiKey && !isKeyGenerated.value) {
    await generateNewTradingApiKey();
  } else {
    emits('closeModal', isKeyGenerated.value || !props.isGenerateApiKey);
  }
}

async function generateNewTradingApiKey(): Promise<void> {
  try {
    const response: TradingApiKeyResponse = await profileService.generateTradingApiKey();
    tradingApiKey.value = response.apiKey;
    isKeyGenerated.value = true;
  } catch (error) {
    console.error('Error generating trading API key:', error);
  }
}
</script>

<template>
  <v-form class="w-100 d-flex align-center">
    <CardComponent with-actions>
      <template #title>
        <span class="text-wrap">
          {{ componentTitle }}
        </span>
      </template>
      <template #item>
        <p class="mb-4">
          {{ componentMessage }}
        </p>
        <div v-if="tradingApiKey != null" class="api-key-container mt-4 pa-3 rounded-lg">
          <div class="d-flex align-center justify-space-between">
            <code class="api-key-text text-body-1 font-weight-medium">{{ tradingApiKey }}</code>
            <v-btn
              :color="copied ? 'success' : 'primary'"
              icon
              size="small"
              variant="text"
              @click="copyToClipboard"
            >
              <v-icon
                :icon="copied
                  ? 'PhCheck'
                  : 'PhCopy'"
              />
              <v-tooltip activator="parent" location="top">
                {{ copied ? t('common.copied') : t('common.copy') }}
              </v-tooltip>
            </v-btn>
          </div>
        </div>

      </template>
      <template #actions>
        <div class="d-flex flex-row justify-space-between ga-4 mt-2">
          <v-btn
            v-if="showCancelButton"
            variant="outlined"
            @click.prevent="emits('closeModal', false)"
          >
            {{ t('common.buttons.cancel') }}
          </v-btn>

          <v-btn
            variant="flat"
            color="primary"
            @click="handleValidate"
          >
            {{ validateButtonText }}
          </v-btn>
        </div>
      </template>
    </CardComponent>
  </v-form>
</template>

<style scoped>
.api-key-container {
  background-color: white;
  border: 1px solid rgb(var(--v-theme-primary));
}

.api-key-text {
  word-break: break-all;
  font-family: monospace;
}
</style>

